import {
  collect504Writes,
  lineMentionsPreferentialSeating,
  lineMentionsSeparateSetting
} from './aeries504AccommodationDiff.js'

const FIELDS = ['instruction', 'assessment', 'reviewDate', 'primaryDisability']
const FLAGS = [
  ['flag1', 'Preferential seating', lineMentionsPreferentialSeating],
  ['flag2', 'Separate setting', lineMentionsSeparateSetting]
]

// Derive the preview from the actual writer's decisions, including blocked
// students, sparse-file gates, flag-only writes and coordinator holds.
export function summarize504Import(diff, decisions = {}, overrides = {}, options = {}) {
  const { writes, blockedHeld } = collect504Writes(diff, decisions, overrides, options)
  const rows = [...(diff.students || []), ...(diff.blocked || [])]
  const byStudent = new Map(rows.map((row) => [row.ssid, row]))
  const byWrite = new Map(writes.map((write) => [write.ssid, write]))
  const flags = new Map((diff.flags || []).map((row) => [row.key, row]))
  const summary = {
    students: writes.length,
    replaced: 0,
    filled: 0,
    kept: 0,
    flagsOn: 0,
    flagsOff: 0,
    blocked: blockedHeld,
    plans: [],
    flagChanges: [],
    conflicts: []
  }
  for (const row of rows) {
    if (!row.write504 && !(row.blocked && overrides[row.ssid])) continue
    const write = byWrite.get(row.ssid)
    for (const change of row.changes || []) {
      if (!FIELDS.includes(change.field)) continue
      if (write?.[change.field] != null) {
        const hasCurrent = String(change.currentRaw ?? change.currentValue ?? '').trim() !== ''
        summary[hasCurrent ? 'replaced' : 'filled']++
      } else summary.kept++
    }
  }
  for (const write of writes) {
    const row = byStudent.get(write.ssid)
    if (write.acceptPlan) summary.plans.push({ ssid: write.ssid, studentName: write.studentName })
    for (const [field, label, detects] of FLAGS) {
      if (typeof write[field] !== 'boolean') continue
      const flag = flags.get(`${write.ssid}|${field}`)
      const item = {
        key: `${write.ssid}|${field}`,
        ssid: write.ssid,
        studentName: write.studentName || flag?.studentName || write.ssid,
        label,
        on: write[field]
      }
      summary.flagChanges.push(item)
      summary[write[field] ? 'flagsOn' : 'flagsOff']++
      if (write[field]) continue
      const current = row?.current || flag?.currentBackup || {}
      const retained = ['instruction', 'assessment'].flatMap((kind) => {
        const text =
          write[kind] ??
          current[kind] ??
          flag?.preview?.[kind === 'instruction' ? 'appInstruction' : 'appAssessment'] ??
          ''
        return detects({ detail: text }).hit ? [{ field: kind, text }] : []
      })
      if (retained.length) summary.conflicts.push({ ...item, retained })
    }
  }
  return summary
}

// Display-only comparison: preserve every character and render with Vue text
// nodes. Bound the LCS work for unusually long imported narratives.
export function compare504Text(current = '', incoming = '') {
  const a = String(current).match(/\s+|[^\s]+/g) || []
  const b = String(incoming).match(/\s+|[^\s]+/g) || []
  const left = [],
    right = []
  const add = (parts, text, changed) => {
    if (!text) return
    if (parts.at(-1)?.changed === changed) parts[parts.length - 1].text += text
    else parts.push({ text, changed })
  }
  if (a.length * b.length > 250000) {
    let start = 0,
      endA = a.length,
      endB = b.length
    while (start < endA && start < endB && a[start] === b[start]) start++
    while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
      endA--
      endB--
    }
    for (const [tokens, end, parts] of [
      [a, endA, left],
      [b, endB, right]
    ]) {
      add(parts, tokens.slice(0, start).join(''), false)
      add(parts, tokens.slice(start, end).join(''), true)
      add(parts, tokens.slice(end).join(''), false)
    }
    return { current: left, incoming: right }
  }
  const lengths = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1))
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--)
      lengths[i][j] =
        a[i] === b[j] ? lengths[i + 1][j + 1] + 1 : Math.max(lengths[i + 1][j], lengths[i][j + 1])
  }
  let i = 0,
    j = 0
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      add(left, a[i++], false)
      add(right, b[j++], false)
    } else if (i < a.length && (j === b.length || lengths[i + 1][j] >= lengths[i][j + 1])) {
      add(left, a[i++], true)
    } else add(right, b[j++], true)
  }
  return { current: left, incoming: right }
}
